const COMPLETED_PLATFORM_FEE_STATES = new Set([
  "PAID",
  "MANUALLY_CONFIRMED"
]);

const ACTIVE_OPERATION_STATES = new Set([
  "ACTIVE_INSTALLMENT_OPERATION",
  "COMPLETED"
]);

export const REQUIRED_APPROVAL_CHECKS = Object.freeze([
  "serialMatchesOperation",
  "sameMerchant",
  "sameCustomer",
  "allPagesPresent",
  "productDataClear",
  "priceAndScheduleMatchSystem",
  "platformFeeVisibleAsIndependentClause",
  "merchantSignaturePresent",
  "customerSignaturePresent",
  "scanQualityAcceptable",
  "noUnacceptableEdits",
  "acceptableForApproval"
]);

const CONTRACT_REVIEW_STATUS_LABELS = Object.freeze({
  en: Object.freeze({
    CONTRACT_UNDER_REVIEW: "Contract under review",
    UNDER_REVIEW: "Contract under review",
    PENDING_ADMIN_REVIEW: "Contract under review",
    PENDING_REVIEW: "Contract under review",
    APPROVED: "Approved",
    REUPLOAD_REQUIRED: "Corrected upload required",
    REJECTED: "Permanently rejected"
  }),
  ar: Object.freeze({
    CONTRACT_UNDER_REVIEW: "العقد قيد المراجعة",
    UNDER_REVIEW: "العقد قيد المراجعة",
    PENDING_ADMIN_REVIEW: "العقد قيد المراجعة",
    PENDING_REVIEW: "العقد قيد المراجعة",
    APPROVED: "معتمد",
    REUPLOAD_REQUIRED: "مطلوب رفع نسخة مصححة",
    REJECTED: "مرفوض نهائيًا"
  })
});

const UNKNOWN_REVIEW_STATUS = Object.freeze({
  en: "Unknown contract review state",
  ar: "حالة مراجعة العقد غير معروفة"
});

const safeDiagnosticCode = (value) =>
  String(value || "UNKNOWN")
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64) || "UNKNOWN";

export const getContractReviewStatusPresentation = (status, language = "en") => {
  const locale = CONTRACT_REVIEW_STATUS_LABELS[language] ? language : "en";
  const code = safeDiagnosticCode(status);
  const label = CONTRACT_REVIEW_STATUS_LABELS[locale][code];
  return label
    ? { label, diagnosticCode: code, known: true }
    : {
        label: UNKNOWN_REVIEW_STATUS[locale],
        diagnosticCode: code,
        known: false
      };
};

export const validateReviewSubmission = ({
  decision,
  reason,
  checklist = {}
} = {}) => {
  const normalizedDecision = String(decision || "").toUpperCase();
  const normalizedReason = String(reason || "").trim();
  if (normalizedDecision === "APPROVED") {
    const missing = REQUIRED_APPROVAL_CHECKS.filter(
      (key) => checklist[key] !== true
    );
    return { valid: missing.length === 0, missing };
  }
  if (["REUPLOAD_REQUIRED", "REJECTED"].includes(normalizedDecision)) {
    return {
      valid: normalizedReason.length >= 3,
      reason: normalizedReason,
      code:
        normalizedReason.length >= 3
          ? undefined
          : "CONTRACT_REVIEW_REASON_REQUIRED"
    };
  }
  return { valid: false, code: "REVIEW_DECISION_REQUIRED" };
};

export const OPERATION_STATUS_FILTER_OPTIONS = Object.freeze([
  "REQUEST_SUBMITTED",
  "MERCHANT_OFFER_CREATED",
  "CUSTOMER_OFFER_ACCEPTED",
  "WAITING_FOR_SIGNATURE",
  "WAITING_FOR_PLATFORM_ADMIN_CONTRACT_REVIEW",
  "WAITING_FOR_PLATFORM_FEE_PAYMENT",
  "CONTRACT_REJECTED",
  "REUPLOAD_REQUIRED",
  "CONTRACT_APPROVED",
  "ACTIVE_INSTALLMENT_OPERATION",
  "COMPLETED",
  "CANCELLED",
  "IN_DISPUTE"
]);

const present = (value) =>
  value !== undefined && value !== null && value !== "";

export const getPublicOperationReference = (operation = {}) =>
  String(operation?.operationReference || "").trim() || "—";

export const getOperationCapabilities = ({
  operation = {},
  contract,
  fee
} = {}) => {
  const actions = operation.availableAdminActions || {};
  const contractId =
    contract?.contract || operation.generatedContract?.contract;
  const signedAttempt =
    contract?.latestSignedUploadAttempt || operation.signedUpload;
  const reviewStatus =
    contract?.reviewStatus || operation.contractReviewStatus;
  const feeState =
    fee?.feeState ||
    operation.platformFee?.feeState ||
    operation.platformFeeStatus;
  const operationStatus = operation.operationStatus;
  const activationGateReached = [
    "signedUploadGate",
    "adminApprovalGate",
    "platformFeeGate",
    "generatedPdfGate",
    "activeTrackingGate",
    "canActivate"
  ].some((key) => operation.activationGates?.[key] === true);

  return {
    hasContract: Boolean(
      contractId ||
        contract?.serial ||
        operation.generatedContract?.serial ||
        operation.generatedContract?.hasGeneratedFile
    ),
    hasSignedUpload: Boolean(signedAttempt?.id),
    hasReviewHistory: Boolean(
      contract?.latestReviewDecision ||
        operation.review ||
        (reviewStatus &&
          !["NOT_SUBMITTED", "PENDING_ADMIN_REVIEW"].includes(reviewStatus))
    ),
    hasPlatformFee: Boolean(
      fee ||
        present(operation.platformFee?.expectedFeeAmount) ||
        present(operation.platformFeeAmount) ||
        (contractId && operation.platformFeeStatus)
    ),
    hasActivationWorkflow: Boolean(
      contractId ||
        signedAttempt?.id ||
        activationGateReached ||
        ACTIVE_OPERATION_STATES.has(operationStatus)
    ),
    hasDispute: Boolean(
      operation.dispute?.hasDispute ||
        operation.disputeStatus === "IN_DISPUTE"
    ),
    canReviewContract:
      actions.canReviewContract ??
      contract?.canReview ??
      Boolean(
        signedAttempt?.id &&
          ["PENDING_ADMIN_REVIEW", "PENDING_REVIEW"].includes(reviewStatus)
      ),
    canConfirmPlatformFee:
      Boolean(contractId) &&
      (actions.canConfirmPlatformFee ??
        fee?.canManualConfirm ??
        !COMPLETED_PLATFORM_FEE_STATES.has(feeState)),
    canActivate: false,
    automaticActivation: true,
    isActive: ACTIVE_OPERATION_STATES.has(operationStatus),
    feeComplete: COMPLETED_PLATFORM_FEE_STATES.has(feeState)
  };
};

export const getOperationDrawerTabs = ({
  operation = {},
  contract,
  fee,
  dispute
} = {}) => {
  const capabilities = getOperationCapabilities({
    operation,
    contract,
    fee
  });
  const tabs = ["overview"];

  if (capabilities.hasContract) tabs.push("files");
  if (
    capabilities.hasSignedUpload ||
    capabilities.hasReviewHistory ||
    capabilities.canReviewContract
  ) {
    tabs.push("review");
  }
  if (capabilities.hasPlatformFee) tabs.push("fee");
  if (
    capabilities.hasActivationWorkflow ||
    capabilities.canActivate ||
    capabilities.isActive
  ) {
    tabs.push("activation");
  }
  tabs.push("events");
  if (dispute?.hasDispute || capabilities.hasDispute) tabs.push("dispute");

  return tabs;
};
